/**
 * AnchorLinks Component
 *
 * Custom component for displaying anchor links as a horizontal swiper.
 * Used in ShowroomDetails for the relatedPages field.
 */

import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import Link from '@salesforce/retail-react-app/app/components/link';
import {
  Box,
  Link as ChakraLink,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import {ArrowBottomRightIcon} from '@amblique/king-living/app/components/icons';
import {scrollElementBelowStickyHeader} from '@amblique/king-living/app/utils/utils';

/**
 * Single anchor link item component
 */
const AnchorLinkItem = ({link, linkStyles}) => {
  const isExternal = link.url?.startsWith('http') || link.openInNewTab;
  const isHashLink = link.url?.startsWith('#');
  const label = link.linkText ?? link.text;
  const key = link.sys?.id || link.url || label;

  const content = (
    <>
      {label}
      <ArrowBottomRightIcon boxSize={{base: '14px', lg: '16px'}} />
    </>
  );

  // Handle hash links with smooth scrolling
  const handleHashClick = (e) => {
    e.preventDefault();

    scrollElementBelowStickyHeader(link.url, () => {
      if (typeof window !== 'undefined') {
        window.history.pushState(null, '', link.url);
      }
    });
  };

  if (isHashLink) {
    return (
      <ChakraLink
        key={key}
        href={link.url}
        {...linkStyles}
        onClick={handleHashClick}
      >
        {content}
      </ChakraLink>
    );
  }

  if (isExternal) {
    return (
      <ChakraLink
        key={key}
        href={link.url}
        isExternal={link.openInNewTab}
        {...linkStyles}
      >
        {content}
      </ChakraLink>
    );
  }

  return (
    <ChakraLink
      key={key}
      as={Link}
      to={link.url}
      {...linkStyles}
    >
      {content}
    </ChakraLink>
  );
};

AnchorLinkItem.propTypes = {
  link: PropTypes.shape({
    linkText: PropTypes.string,
    text: PropTypes.string,
    url: PropTypes.string.isRequired,
    openInNewTab: PropTypes.bool,
    sys: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  linkStyles: PropTypes.object,
};

export const AnchorLinks = ({links = [], containerProps = {}, ...props}) => {
  const styles = useMultiStyleConfig('contentful/showroom-details');

  const swiperOptions = useMemo(() => ({
    autoHeight: false,
    freeMode: true,
    navigation: true,
    nested: true,
    pagination: false,
    scrollbar: false,
    slidesPerGroup: 1,
    slidesPerView: 'auto',
    spaceBetween: '16px',
  }), []);

  if (!links || links.length === 0) {
    return null;
  }

  return (
    <Box
      className="anchor-links-container"
      position="relative"
      {...containerProps}
      {...props}
    >
      <Box {...styles.anchorLinksSwiperWrapper}>
        <Swiper
          className="anchor-links-swiper"
          options={swiperOptions}
          presets=""
          variant="anchor-links"
          {...styles.anchorLinksSwiper}
        >
          {links.map((link) => (
            <Box key={link.sys?.id || link.url || link.linkText || link.text}>
              <AnchorLinkItem link={link} linkStyles={styles.anchorLinksItem} />
            </Box>
          ))}
        </Swiper>
      </Box>
    </Box>
  );
};

AnchorLinks.displayName = 'AnchorLinks';

AnchorLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      openInNewTab: PropTypes.bool,
      sys: PropTypes.shape({
        id: PropTypes.string,
      }),
    })
  ),
  containerProps: PropTypes.object,
};

export default AnchorLinks;
